import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getEvaluacionesPendientes,
  getFormulario,
  getFormularioPendiente,
  verificarTiempoEvaluacion,
  type EvaluacionPendiente,
  type FormularioPendiente,
} from '../../api/formularios';
import { useAuth } from '../../context/AuthContext';
import gorilaAttentionImg from '../../imagenes/Gorilas/gorila_attention.png';

import FormularioRespuestaDialog from './FormularioRespuestaDialog';
import EvaluacionConTiempoDialog from './EvaluacionConTiempoDialog';

type PendienteItem =
  | { type: 'formulario'; data: FormularioPendiente }
  | { type: 'evaluacion'; data: EvaluacionPendiente };

export default function FormularioBlocker() {
  const { user } = useAuth();
  const [formularioPendiente, setFormularioPendiente] = useState<FormularioPendiente | null>(null);
  const [evaluacionesPendientes, setEvaluacionesPendientes] = useState<EvaluacionPendiente[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [allPendientes, setAllPendientes] = useState<PendienteItem[]>([]);
  const [selectedPendienteIndex, setSelectedPendienteIndex] = useState(0);
  const [completedIds, setCompletedIds] = useState<Set<number>>(new Set());

  const loadPendientes = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // 1) Formulario pendiente (solo formularios, no evaluaciones)
      const pendiente = await getFormularioPendiente();
      let formulario: FormularioPendiente | null = null;

      if (pendiente) {
        const formularioCompleto = await getFormulario(pendiente.id);
        if (!formularioCompleto.esEvaluacion) {
          formulario = pendiente;
        }
      }

      // 2) Evaluaciones pendientes del usuario (filtradas por expiración)
      const evaluaciones = await getEvaluacionesPendientes();
      const evaluacionesUsuario = evaluaciones.filter((e) => e.usuarioId === user.id);

      const evaluacionesVigentes: EvaluacionPendiente[] = [];
      for (const evaluacion of evaluacionesUsuario) {
        try {
          const estado = await verificarTiempoEvaluacion(evaluacion.formularioId);
          if (estado?.expirada === true) continue;
          evaluacionesVigentes.push(evaluacion);
        } catch (error) {
          console.error('Error verificando evaluación:', error);
          // Fallback: si falla la verificación, mostramos igualmente
          evaluacionesVigentes.push(evaluacion);
        }
      }

      const completedSet = completedIds;

      const evaluacionesFiltradas = evaluacionesVigentes.filter(
        (e) => !completedSet.has(e.formularioId),
      );

      const evaluacionesUnicasMap = new Map<number, EvaluacionPendiente>();
      evaluacionesFiltradas.forEach((e) => {
        if (!evaluacionesUnicasMap.has(e.formularioId)) {
          evaluacionesUnicasMap.set(e.formularioId, e);
        }
      });
      const evaluacionesUnicas = Array.from(evaluacionesUnicasMap.values());

      const formularioFiltrado =
        formulario && !completedSet.has(formulario.id) ? formulario : null;

      setFormularioPendiente(formularioFiltrado);
      setEvaluacionesPendientes(evaluacionesUnicas);

      const pendientes: PendienteItem[] = [];
      if (formularioFiltrado) pendientes.push({ type: 'formulario', data: formularioFiltrado });
      evaluacionesUnicas.forEach((evaluacion) =>
        pendientes.push({ type: 'evaluacion', data: evaluacion }),
      );
      setAllPendientes(pendientes);
      setSelectedPendienteIndex(0);
    } catch (error) {
      console.error('Error cargando pendientes:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, completedIds]);

  useEffect(() => {
    loadPendientes();

    const onCompleted = (event: Event) => {
      const custom = event as CustomEvent<{ formularioId?: number }>;
      const formularioId = custom?.detail?.formularioId;

      if (formularioId) {
        setCompletedIds((prev) => {
          const next = new Set(prev);
          next.add(formularioId);
          return next;
        });

        setFormularioPendiente((prev) => {
          if (prev?.id === formularioId) return null;
          return prev;
        });

        setEvaluacionesPendientes((prev) =>
          prev.filter((e) => e.formularioId !== formularioId),
        );
      }

      if (formularioId) {
        setAllPendientes((prev) => {
          const next = prev.filter((item) => {
            if (item.type === 'formulario') return item.data.id !== formularioId;
            return item.data.formularioId !== formularioId;
          });

          if (next.length === 0) {
            setFormularioPendiente(null);
            setEvaluacionesPendientes([]);
            setShowDialog(false);
            setCurrentFormIndex(0);
            setSelectedPendienteIndex(0);
          } else {
            setSelectedPendienteIndex(0);
          }

          return next;
        });
      } else {
        setShowDialog(false);
        setCurrentFormIndex(0);
      }

      setTimeout(() => loadPendientes(), 500);
    };

    window.addEventListener('formulario:completed', onCompleted);
    return () => window.removeEventListener('formulario:completed', onCompleted);
  }, [loadPendientes]);

  const handleCloseBlocker = () => {
    setFormularioPendiente(null);
    setEvaluacionesPendientes([]);
    setAllPendientes([]);
    setShowDialog(false);
    setCurrentFormIndex(0);
    setSelectedPendienteIndex(0);
  };

  const handleCompletarAhora = () => {
    if (allPendientes.length === 0) return;
    setCurrentFormIndex(selectedPendienteIndex);
    setShowDialog(true);
  };

  const handleFormComplete = () => {
    setShowDialog(false);
    setCurrentFormIndex(0);
    setFormularioPendiente(null);
    setEvaluacionesPendientes([]);
    setAllPendientes([]);
    setSelectedPendienteIndex(0);
    window.dispatchEvent(new Event('formulario:completed'));
  };

  const handleFormClose = () => {
    setShowDialog(false);
    setCurrentFormIndex(0);
  };

  if (loading) return null;

  const tienePendientes = formularioPendiente || evaluacionesPendientes.length > 0;
  if (!tienePendientes) return null;

  const numFormularios = formularioPendiente ? 1 : 0;
  const numEvaluaciones = evaluacionesPendientes.length;

  return (
    <>
      <Dialog
        open
        onClose={handleCloseBlocker}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'visible',
            position: 'relative',
            mt: '120px',
            maxHeight: 'calc(100vh - 240px)',
          },
        }}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={gorilaAttentionImg}
          alt="Gorila Attention"
          sx={{
            position: 'absolute',
            left: '50%',
            top: '-220px',
            transform: 'translateX(-50%)',
            width: '250px',
            height: 'auto',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />

        <DialogTitle
          sx={{
            backgroundColor: '#000',
            color: '#fff',
            fontWeight: 'bold',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            pr: { xs: 2, sm: 10 },
          }}
        >
          ¡Ey! ¿Donde vas tan rápido?
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: '#fff', pt: 0.5, pb: 0.5, textAlign: 'center' }}>
          <Typography variant="body1" color="text.primary" sx={{ mb: 2, mt: 3 }}>
            Esta selva necesita estar a la orden del día, ¡así que no te hagas de rogar y no dejes cosas pendientes!
          </Typography>

          <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontWeight: 'medium' }}>
            Tienes pendiente rellenar:{' '}
            <span style={{ color: '#21a598' }}>
              {numFormularios > 0 && `${numFormularios} FORMULARIO${numFormularios !== 1 ? 'S' : ''}`}
              {numFormularios > 0 && numEvaluaciones > 0 && ' y '}
              {numEvaluaciones > 0 && `${numEvaluaciones} EVALUACION${numEvaluaciones !== 1 ? 'ES' : ''}`}
            </span>
          </Typography>

          {allPendientes.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                Elige cuál quieres abrir:
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={String(selectedPendienteIndex)}
                  onChange={(e) => setSelectedPendienteIndex(Number(e.target.value))}
                >
                  {allPendientes.map((pendiente, index) => {
                    const label =
                      pendiente.type === 'formulario'
                        ? `Formulario: ${pendiente.data.titulo}`
                        : `Evaluación: ${pendiente.data.formularioTitulo}`;
                    return (
                      <FormControlLabel
                        key={`${pendiente.type}-${index}`}
                        value={String(index)}
                        control={<Radio />}
                        label={label}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            backgroundColor: '#fff',
            mt: 0,
            px: 2,
            py: 2,
            flexDirection: 'column',
            gap: 1,
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
          }}
        >
          <Button
            onClick={handleCompletarAhora}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: '#21a598',
              color: '#fff',
              '&:hover': { backgroundColor: '#1a8578' },
              textTransform: 'uppercase',
              fontWeight: 'bold',
            }}
          >
            ABRIR SELECCIONADO
          </Button>
          <Button
            onClick={handleCloseBlocker}
            sx={{
              color: '#EB0000',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(150, 149, 149, 0.08)',
              },
            }}
          >
            Rellenar más tarde
          </Button>
        </DialogActions>
      </Dialog>

      {showDialog &&
        allPendientes.length > 0 &&
        currentFormIndex < allPendientes.length &&
        allPendientes[currentFormIndex]?.type === 'formulario' && (
          <FormularioRespuestaDialog
            open={showDialog}
            formulario={(allPendientes[currentFormIndex] as Extract<PendienteItem, { type: 'formulario' }>).data}
            onClose={handleFormClose}
            onComplete={handleFormComplete}
          />
        )}

      {showDialog &&
        allPendientes.length > 0 &&
        currentFormIndex < allPendientes.length &&
        allPendientes[currentFormIndex]?.type === 'evaluacion' && (
          <EvaluacionDialogWrapper
            evaluacion={(allPendientes[currentFormIndex] as Extract<PendienteItem, { type: 'evaluacion' }>).data}
            open={showDialog}
            onClose={handleFormClose}
            onComplete={handleFormComplete}
          />
        )}
    </>
  );
}

function EvaluacionDialogWrapper({
  evaluacion,
  open,
  onClose,
  onComplete,
}: {
  evaluacion: EvaluacionPendiente;
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}) {
  const [formularioData, setFormularioData] = useState<FormularioPendiente | null>(null);
  const [loading, setLoading] = useState(true);

  const shouldLoad = useMemo(() => open && !!evaluacion, [open, evaluacion]);

  useEffect(() => {
    if (!shouldLoad) return;

    let cancelled = false;
    const loadEvaluacion = async () => {
      setLoading(true);
      setFormularioData(null);

      try {
        const estado = await verificarTiempoEvaluacion(evaluacion.formularioId);
        if (estado?.expirada === true) {
          if (!cancelled) onComplete();
          return;
        }

        const evaluacionCompleta = await getFormulario(evaluacion.formularioId);
        if (cancelled) return;

        setFormularioData({
          id: evaluacionCompleta.id,
          titulo: evaluacionCompleta.titulo,
          descripcion: evaluacionCompleta.descripcion,
          fechaLimite: evaluacionCompleta.fechaLimite,
          tiempoLimite: evaluacionCompleta.tiempoLimite,
          preguntas: evaluacionCompleta.preguntas || [],
        });
      } catch (error) {
        console.error('Error al cargar/verificar evaluación:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEvaluacion();
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, evaluacion, onComplete]);

  if (loading || !formularioData) return null;

  return formularioData.tiempoLimite ? (
    <EvaluacionConTiempoDialog
      open={open}
      formulario={formularioData}
      onClose={onClose}
      onComplete={onComplete}
      autoCloseOnResult
    />
  ) : (
    <FormularioRespuestaDialog
      open={open}
      formulario={formularioData}
      onClose={onClose}
      onComplete={onComplete}
      autoCloseOnResult
      autoSubmitOnCancel
    />
  );
}
